import { useState, useEffect } from 'react';
import { useStore } from '../lib/store';
import { supabase, supabaseEnabled } from '../lib/supabase';
import { Icon } from '../components/Icon';
import { TabBar } from '../components/ui';
import { VOICES } from '../lib/tts';
import { clearLibrary } from '../lib/db';
import { detectOnDeviceCapability, getDiagnostics, type OnDeviceStatus } from '../lib/kokoro-ondevice';

export function Settings() {
  const { theme, setTheme, font, setFont, fontSize, setFontSize, voice, setVoice, session, autoScrollWpm, setAutoScrollWpm, refreshBooks } = useStore();
  const [confirmClear, setConfirmClear] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [onDeviceStatus, setOnDeviceStatus] = useState<OnDeviceStatus>('unknown');
  const [diag, setDiag] = useState<Record<string, any> | null>(null);

  useEffect(() => { detectOnDeviceCapability().then(setOnDeviceStatus); }, []);
  const runDiag = async () => { setDiag(await getDiagnostics()); };

  const signOut = async () => {
    if (supabaseEnabled) await supabase.auth.signOut();
  };

  const clearAll = async () => {
    await clearLibrary();
    await refreshBooks();
    setCleared(true);
    setConfirmClear(false);
    setTimeout(() => setCleared(false), 3000);
  };

  return (
    <div className="flex flex-col h-full safe-top">
      <div className="px-5 pt-6">
        <h1 className="font-display text-[34px] leading-none">Settings</h1>
        <p className="text-[13px] text-ink-500 mt-1">{session?.user?.email || 'local only'}</p>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar pb-28 pt-5">
        {/* Theme */}
        <div className="px-5 mb-6">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Theme</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'light' as const, label: 'Paper', bg: '#FAF7F0', fg: '#1A1A1A' },
              { id: 'sepia' as const, label: 'Sepia', bg: '#EEE1C6', fg: '#3A2515' },
              { id: 'dark' as const, label: 'Night', bg: '#14120F', fg: '#F1EBDC' },
            ].map((t) => (
              <button key={t.id} onClick={() => setTheme(t.id)} className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-1 border ${theme === t.id ? 'border-forest-500' : 'border-transparent'}`} style={{ background: t.bg, color: t.fg }}>
                <span className="font-serif text-[22px]">Aa</span>
                <span className="text-[10px]">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Font */}
        <div className="px-5 mb-6">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Font</div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { name: 'Fraunces', sub: 'Editorial' },
              { name: 'Instrument Serif', sub: 'Classic' },
              { name: 'Georgia', sub: 'Serif' },
              { name: 'Inter', sub: 'Sans' },
            ].map((f) => (
              <button key={f.name} onClick={() => setFont(f.name)} className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-0.5 transition ${font === f.name ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'bg-paper dark:bg-night-800 shadow-soft'}`}>
                <span className="text-[22px]" style={{ fontFamily: f.name }}>Aa</span>
                <span className="text-[10px] opacity-70">{f.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Size */}
        <div className="px-5 mb-6">
          <div className="flex justify-between text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">
            <span>Reader text size</span><span className="tabular-nums">{fontSize}px</span>
          </div>
          <input type="range" min={12} max={22} value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} className="w-full accent-ink-900 dark:accent-cream-100" />
        </div>

        {/* Auto-scroll */}
        <div className="px-5 mb-6">
          <div className="flex justify-between text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">
            <span>Auto-scroll speed</span><span className="tabular-nums">{autoScrollWpm} wpm</span>
          </div>
          <input type="range" min={120} max={400} step={10} value={autoScrollWpm} onChange={(e) => setAutoScrollWpm(Number(e.target.value))} className="w-full accent-coral" />
        </div>

        {/* Voice engine status */}
        <div className="px-5 mb-6">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Voice engine</div>
          <div className="w-full p-4 rounded-2xl bg-paper dark:bg-night-800 shadow-soft">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[13px] font-semibold">Kokoro (on-device)</div>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${onDeviceStatus === 'webgpu' ? 'bg-forest-500/10 text-forest-500' : onDeviceStatus === 'wasm' ? 'bg-amber-500/10 text-amber-600' : 'bg-coral/10 text-coral'}`}>
                {onDeviceStatus === 'webgpu' && 'WebGPU - fast'}
                {onDeviceStatus === 'wasm' && 'WASM - slower'}
                {onDeviceStatus === 'unknown' && 'detecting...'}
                {onDeviceStatus === 'unavailable' && 'unavailable'}
              </span>
            </div>
            <p className="text-[11px] text-ink-500">Runs entirely on your phone. One-time download ({onDeviceStatus === 'webgpu' ? '~150MB fp16' : '~80MB q8'}), fully offline after. Cached in browser storage.</p>
            {onDeviceStatus === 'wasm' && (
              <p className="text-[11px] text-amber-600 mt-1.5">Running in WASM mode (slower). Update to iOS 26+ or Chrome with WebGPU for faster speech.</p>
            )}
            <button onClick={runDiag} className="mt-3 h-9 px-4 rounded-full bg-black/5 dark:bg-white/10 text-[11px] font-medium">Run diagnostics</button>
            {diag && (
              <pre className="mt-3 p-2 rounded-lg bg-night-900 text-cream-50 text-[10px] font-mono whitespace-pre-wrap overflow-x-auto">{JSON.stringify(diag, null, 2)}</pre>
            )}
          </div>
        </div>

        {/* Voice */}
        <div className="px-5 mb-6">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Voice</div>
          <div className="grid grid-cols-2 gap-2">
            {VOICES.map((v) => (
              <button key={v.id} onClick={() => setVoice(v.id)} className={`p-3 rounded-2xl text-left transition ${voice === v.id ? 'ring-2 ring-forest-500' : ''}`} style={{ background: v.color + '1a' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full text-cream-50 flex items-center justify-center text-[11px] font-semibold" style={{ background: v.color }}>{v.name[0]}</div>
                  <div>
                    <div className="text-[13px] font-medium">{v.name}</div>
                    <div className="text-[10px] text-ink-500">{v.sub}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-ink-500 mt-2">Kokoro on-device voices. First play downloads ~150MB of model weights (cached for offline use).</p>
        </div>

        {/* Account */}
        {supabaseEnabled && session && (
          <div className="px-5 mb-6">
            <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Account</div>
            <div className="rounded-2xl bg-paper dark:bg-night-800 shadow-soft p-4">
              <div className="text-[13px] font-medium">{session.user.email}</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Highlights sync to SiYuan via Supabase</div>
              <button onClick={signOut} className="mt-3 text-[12px] text-coral font-medium flex items-center gap-1.5">
                Sign out
              </button>
            </div>
          </div>
        )}

        {/* Danger zone: wipe local library. Useful before building SiYuan sync. */}
        <div className="px-5 mb-6">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Danger zone</div>
          <div className="rounded-2xl bg-paper dark:bg-night-800 shadow-soft p-4">
            <div className="text-[13px] font-medium">Clear library</div>
            <p className="text-[11px] text-ink-500 mt-0.5">Wipes all books, files, chapters, and highlights from this phone. Nothing in SiYuan yet to sync from, so this is destructive.</p>
            {cleared ? (
              <div className="mt-3 text-[12px] text-forest-500 font-medium flex items-center gap-1.5">
                <Icon name="check" className="w-4 h-4" />Library cleared
              </div>
            ) : !confirmClear ? (
              <button onClick={() => setConfirmClear(true)} className="mt-3 h-10 px-4 rounded-full bg-coral/10 text-coral text-[12px] font-medium">
                Clear everything
              </button>
            ) : (
              <div className="mt-3 flex gap-2">
                <button onClick={clearAll} className="flex-1 h-10 rounded-full bg-coral text-cream-50 text-[12px] font-semibold">
                  Confirm wipe
                </button>
                <button onClick={() => setConfirmClear(false)} className="flex-1 h-10 rounded-full bg-black/5 dark:bg-white/10 text-[12px] font-medium">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 mt-2 text-[10px] text-ink-500 text-center">
          Doomscroll v0.1 · <Icon name="book" className="w-3 h-3 inline-block" /> read your library, not the feed
        </div>
      </div>

      <TabBar active="settings" />
    </div>
  );
}
